import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  SwipeableDrawer, // <-- Added for Transfer Sheet
  List, // <-- Added for Transfer Sheet
  ListItemButton, // <-- Added for Transfer Sheet
  ListItemIcon, // <-- Added for Transfer Sheet
  ListItemText, // <-- Added for Transfer Sheet
  Typography, // <-- Added for Transfer Sheet
  TextField, // <-- Added for Transfer Sheet
  Stack, // <-- Added for Transfer Sheet
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";
import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";
import CallCenterDialer from "./components/dialer";
import CallHistoryPage from "./components/history";
import DialPad from "./components/dial_pad";
import ContactHistoryDialog from "./components/contact_history";

function CallCenterDialerPage() {
  const pageTitle = `Call Center - Dialer`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();
  const statusDialog = useAwaitDialog();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [callCenter, setCallCenter] = useState({});
  const [contact, setContact] = useState(null);
  const [completedContact, setCompletedContact] = useState(null);

  const [callOutcomes, setCallOutcomes] = useState([]);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [openOutcomeDialog, setOpenOutcomeDialog] = useState(false);

  const [history, setHistory] = useState([]);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);

  // Save Contact Dialog State
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [openSaveContactDialog, setOpenSaveContactDialog] = useState(false);
  const [saveContactPhone, setSaveContactPhone] = useState("");
  const [saveContactName, setSaveContactName] = useState("");

  // --- Active Call Tracking for Auto-Reload ---
  const [isCallActive, setIsCallActive] = useState(false);
  const isCallActiveRef = useRef(false);

  // Keep ref updated to access current status inside setInterval closure
  useEffect(() => {
    isCallActiveRef.current = isCallActive;
  }, [isCallActive]);

  // --- Contact Past Conversation History State ---
  const [contactHistory, setContactHistory] = useState([]);
  const [openContactHistoryDialog, setOpenContactHistoryDialog] =
    useState(false);
  const [activeContactNumber, setActiveContactNumber] = useState("");

  const [openDialPad, setOpenDialPad] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [activeCall, setActiveCall] = useState(null);

  // --- New Transfer Drawer State Controllers ---
  const [activeCallDetails, setActiveCallDetails] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerStep, setDrawerStep] = useState("options");
  const [searchQuery, setSearchQuery] = useState("");
  const [msisdnValue, setMsisdnValue] = useState("");

  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/call_centers/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setCallCenter(result.data.data.center);
            setCallOutcomes(result.data.data.call_outcomes);
            setTags(result.data.data.tags);
            setGroups(result.data.data.groups);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getContact() {
    setShowLoading(true);
    await axios
      .get(`agents/call_centers/${ref}/contact.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setContact(result.data.data.contact);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  }

  async function submitOutcome() {
    const contactRef =
      completedContact.reference ?? completedContact.phone_number;
    if (!contactRef) {
      setSeverity("error");
      setErrMsg("No contact found.");
      setOpen(true);
      return false;
    }

    if (selectedOutcome === null) {
      setSeverity("error");
      setErrMsg("Call outcome is required.");
      setOpen(true);
      return false;
    }

    setOpenOutcomeDialog(false);
    setShowLoading(true);

    const json = {
      reference: ref,
      contact: contactRef,
      outcome: selectedOutcome,
      lat: applocation?.lat,
      lng: applocation?.lng,
    };

    await axios
      .post("agents/call_centers/call/outcome.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(
            result.data.data.message,
            "success",
            "Success!",
          );
          setContact(null);
          setCompletedContact(null);
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async () => {
        setShowLoading(false);
        let err = `Unable to complete request. ${
          navigator.onLine
            ? ""
            : "Make sure you have an active internet connection"
        }`;
        await statusDialog.show(err, "error", "Failed");
      });
  }

  async function getHistroy() {
    setShowLoading(true);
    await axios
      .get(`agents/call_centers/${ref}/history.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setHistory(result.data.data.history);
          setOpenHistoryDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  }

  async function getActiveCall(contactNumber, direction) {
    // setShowLoading(true);
    const url = `agents/call_centers/calls/${ref}/${contactNumber}/${direction}/active.json`;
    await axios
      .get(url)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setActiveCall(result.data.data.call);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  }

  // --- Async Agent Fetching ---
  const fetchAgentsFromBackend = async () => {
    setLoadingAgents(true);
    try {
      const response = await axios.get(
        `agents/call_centers/${ref}/activeAgents.json`,
      );
      if (response.data?.status === "success") {
        setAgents(response.data.data.agents || []);
      } else if (Array.isArray(response.data)) {
        setAgents([]);
      }
    } catch (err) {
      console.error("Failed to load active transfer agents:", err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleTransferInitiated = ({ direction, contact, duration }) => {
    setActiveCallDetails({ direction, contact, duration });
    setDrawerStep("options");
    setSearchQuery("");
    setMsisdnValue("");
    setDrawerOpen(true);
  };

  const handleStepSelection = (step) => {
    setDrawerStep(step);
    setSearchQuery("");
    if (step === "agent") {
      fetchAgentsFromBackend();
    }
  };

  const [transferTracker, setTransferTracker] = useState(0);
  const executeTransfer = async (targetType, targetValue) => {
    setDrawerOpen(false);
    setShowLoading(true);

    const payload = {
      reference: ref,
      call_reference: activeCall.reference,
      destination_type: targetType,
      destination: targetValue,
    };

    try {
      const response = await axios.post(
        `agents/call_centers/call/transfer.json`,
        payload,
      );
      setShowLoading(false);

      if (response.data.status === "success") {
        await statusDialog.show(
          response.data.data.message,
          "success",
          "Call Transferred",
        );
        // Notice child of successful call transfer
        setTransferTracker((prev) => prev + 1);
      } else {
        await statusDialog.show(response.data.data.message, "error", "Error");
      }
    } catch (err) {
      setShowLoading(false);
      await statusDialog.show(
        "Network failure or timeout processing call transfer routing request.",
        "error",
        "Failed",
      );
    }
  };

  // --- Search Filtering Computations ---
  const filteredGroups = groups.filter((g) =>
    g.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredAgents = agents.filter(
    (a) =>
      (a.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.email || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Fetches contact's past conversation history bubbled up from CallCenterDialer during an active call
  async function getContactCallHistory(contactNumber) {
    setShowLoading(true);
    setActiveContactNumber(contactNumber);
    const url = `agents/call_centers/calls/${ref}/${contactNumber}/history.json`;
    await axios
      .get(url)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          // Store conversation history and open the contact history modal
          setContactHistory(
            result.data.data.history || result.data.data.calls || [],
          );
          setOpenContactHistoryDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
        );
        setOpen(true);
      });
  }

  async function getCallerID(contactNumber) {
    const url = `agents/call_centers/contacts/${ref}/${contactNumber}/lookup.json`;
    try {
      const result = await axios.get(url);
      setShowLoading(false);

      if (result.data.status === "success" && result.data.data?.contact?.name) {
        return result.data.data.contact.name;
      }
      return contactNumber;
    } catch (error) {
      setShowLoading(false);
      return contactNumber;
    }
  }

  // Triggered when child component requests saving a contact
  const handleTriggerSaveContact = (phoneNumber) => {
    setSaveContactPhone(phoneNumber);
    setSaveContactName("");
    setOpenSaveContactDialog(true);
  };

  // API submission to save contact
  const handleSaveContactSubmit = async () => {
    if (!saveContactName.trim()) {
      setSeverity("error");
      setErrMsg("Please enter a name for the contact.");
      setOpen(true);
      return;
    }

    setOpenSaveContactDialog(false);
    setShowLoading(true);

    try {
      const res = await axios.post(`agents/call_centers/contacts/create.json`, {
        reference: ref,
        phone_number: saveContactPhone,
        name: saveContactName,
        tag: selectedTag,
      });
      setShowLoading(false);

      if (res.data.status === "success") {
        await statusDialog.show(
          res.data.data.message || "Contact saved successfully!",
          "success",
          "Success",
        );
        setRefreshPage((prev) => !prev);
      } else {
        await statusDialog.show(res.data.data.message, "error", "Failed");
      }
    } catch (err) {
      setShowLoading(false);
      await statusDialog.show(
        "Unable to save contact. Please check your network connection.",
        "error",
        "Failed",
      );
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={openSaveContactDialog}
        onClose={() => setOpenSaveContactDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <MDTypography variant="h6">Save Contact</MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} pt={1}>
            <TextField
              label="Phone Number"
              size="small"
              fullWidth
              value={saveContactPhone}
              disabled
            />

            <TextField
              label="Contact Name"
              size="small"
              fullWidth
              value={saveContactName}
              onChange={(e) => setSaveContactName(e.target.value)}
              autoFocus
              required
            />

            {tags.length > 0 && (
              <MDBox>
                <FormControl sx={{ minWidth: 120, width: "100%" }}>
                  <InputLabel>Tag (Optional)</InputLabel>
                  <Select
                    label="Tag (Optional)"
                    onChange={(e) => {
                      setSelectedTag(e.target.value);
                    }}
                    sx={{ padding: "12px", paddingLeft: "3px" }}
                  >
                    {tags.length > 0 &&
                      tags.map((record) => (
                        <MenuItem
                          key={record.reference}
                          value={`${record.reference}`}
                        >
                          {record.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </MDBox>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="primary"
            variant="outlined"
            onClick={() => setOpenSaveContactDialog(false)}
          >
            Cancel
          </MDButton>
          &nbsp;
          <MDButton
            color="primary"
            variant="contained"
            onClick={handleSaveContactSubmit}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openOutcomeDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Log Call Outcome</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Call Outcome</InputLabel>
                <Select
                  label="Call Outcome"
                  onChange={(e) => {
                    setSelectedOutcome(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {callOutcomes.length > 0 &&
                    callOutcomes.map((record) => (
                      <MenuItem
                        key={record.reference}
                        value={`${record.reference}`}
                      >
                        {record.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="primary"
            variant="outlined"
            onClick={() => {
              setOpenOutcomeDialog(false);
            }}
          >
            Cancel
          </MDButton>
          &nbsp;
          <MDButton
            color="primary"
            variant="contained"
            onClick={() => {
              submitOutcome();
            }}
          >
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={2} mb={5}>
        <MDBox mb={1} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate(`/call-centers/${ref}`)}
            >
              Back
            </Button>
          </MDBox>
          <MDBox mt={0.5} pr={1}>
            <Tooltip title="Refresh Dialer">
              <MDButton
                color="dark"
                variant="outlined"
                size="small"
                iconOnly
                onClick={() => window.location.reload()}
              >
                <Icon>refresh</Icon>
              </MDButton>
            </Tooltip>
            &nbsp;&nbsp;
            <Tooltip title="Call History">
              <MDButton
                iconOnly
                color="dark"
                variant="outlined"
                size="small"
                onClick={() => getHistroy()}
              >
                <Icon>list</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        </MDBox>

        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card>
                <MDBox px={2} py={2}>
                  <MDBox>
                    <ListItem>
                      <MDBox
                        width="100%"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDBox mt={-1}>
                          <MDTypography variant="h6">
                            {callCenter.title}
                          </MDTypography>
                          <MDTypography
                            mt={1}
                            fontSize={12}
                            color="text.secondary"
                          >
                            {callCenter.account.name}
                          </MDTypography>
                        </MDBox>

                        <MDBox>
                          <MDBox>
                            <Icon>support_agent</Icon>
                          </MDBox>
                          <MDBox>
                            <img
                              src={`${AppConfig.api_media_url}${callCenter.account.icon}`}
                              alt=""
                              width="18px"
                            />
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </ListItem>
                  </MDBox>
                </MDBox>

                <MDBox p={0}>
                  <Grid container spacing={3} justifyContent="center" mb={3}>
                    <Grid item xs={12} md={8} lg={8}>
                      <MDBox mt={3}>
                        <MDBox p={2}>
                          <CallCenterDialer
                            isTransferred={transferTracker}
                            agentRef={ref}
                            contact={contact}
                            onIncomingCall={() => {
                              setIsCallActive(true);
                            }}
                            onCallCompleted={(completedContact) => {
                              setIsCallActive(false);

                              if (
                                callOutcomes.length <= 0 ||
                                completedContact === null
                              ) {
                                return;
                              }

                              setCompletedContact(completedContact);
                              setOpenOutcomeDialog(true);
                            }}
                            onTransferCall={handleTransferInitiated}
                            onCallActive={async ({
                              callerNumber,
                              direction,
                            }) => {
                              setIsCallActive(true);
                              await getActiveCall(callerNumber, direction);
                            }}
                            onFetchHistory={async (callerNumber) =>
                              getContactCallHistory(callerNumber)
                            }
                            onFetchCallerId={getCallerID}
                            onSaveContact={handleTriggerSaveContact}
                          />

                          {/* Top-bar Call Center History component */}
                          <CallHistoryPage
                            open={openHistoryDialog}
                            onClose={() => setOpenHistoryDialog(false)}
                            calls={history}
                            onSelectContact={(contact) => {
                              setContact(contact);
                            }}
                          />

                          {/* Active Call - Contact Past Conversation History component */}
                          <ContactHistoryDialog
                            open={openContactHistoryDialog}
                            onClose={() => setOpenContactHistoryDialog(false)}
                            history={contactHistory}
                            contactNumber={activeContactNumber}
                          />
                        </MDBox>
                      </MDBox>

                      {callCenter.can_call && (
                        <MDBox mt={5} px={1}>
                          <Grid
                            container
                            spacing={{
                              xs: 1,
                              md: 3,
                              lg: 3,
                            }}
                          >
                            <Grid item xs={6} md={6} lg={6}>
                              <MDBox>
                                <MDButton
                                  variant="contained"
                                  color="primary"
                                  fullWidth
                                  startIcon={<Icon>contact_phone</Icon>}
                                  onClick={() => {
                                    setContact(null);
                                    getContact();
                                  }}
                                >
                                  Contact
                                </MDButton>
                              </MDBox>
                            </Grid>
                            <Grid item xs={6} md={6} lg={6}>
                              <MDBox>
                                <MDButton
                                  variant="contained"
                                  color="primary"
                                  fullWidth
                                  startIcon={<Icon>dialpad</Icon>}
                                  onClick={() => {
                                    setOpenDialPad(true);
                                  }}
                                  disabled={!callCenter.can_dial}
                                >
                                  Dial Pad
                                </MDButton>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </MDBox>
                      )}
                      <DialPad
                        open={openDialPad}
                        onClose={() => setOpenDialPad(false)}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        onSubmit={(number) => {
                          setContact({
                            name: number,
                            phone_number: number,
                          });

                          setOpenDialPad(false);
                        }}
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <SwipeableDrawer
        anchor={window.innerWidth < 600 ? "bottom" : "right"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        ModalProps={{ keepMounted: true }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            maxWidth: "100vw",
            margin: 0,
            overflow: "visible",
            "@media (max-width:599px)": {
              width: "100vw",
              left: 0,
              right: 0,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              height: "auto",
              minHeight: "60vh",
              paddingBottom: 3,
            },
            "@media (min-width:600px)": {
              position: "fixed",
              top: "0 !important",
              right: "0 !important",
              width: 420,
              height: "100vh",
              borderRadius: 0,
            },
          },
        }}
      >
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            height: { xs: "auto", sm: "100vh" },
          }}
        >
          <MDBox
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {drawerStep !== "options" && (
                <IconButton
                  onClick={() => setDrawerStep("options")}
                  size="small"
                >
                  <Icon>arrow_back</Icon>
                </IconButton>
              )}
              <MDTypography variant="h6" fontSize="1.1rem">
                {drawerStep === "options" && "Transfer Call"}
                {drawerStep === "group" && "Select Group"}
                {drawerStep === "agent" && "Select Teammate"}
                {drawerStep === "msisdn" && "Enter Phone Number"}
              </MDTypography>
            </Stack>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
          <Divider sx={{ my: 0 }} />

          <MDBox sx={{ flexGrow: 1, overflowY: "auto" }}>
            {drawerStep === "options" && (
              <List sx={{ py: 1 }}>
                <ListItem divider disablePadding sx={{ my: 1 }}>
                  <ListItemButton
                    onClick={() => handleStepSelection("group")}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: "8px",
                      minWidth: 0,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <MDTypography fontSize={32} color="primary">
                        <Icon>groups</Icon>
                      </MDTypography>
                    </ListItemIcon>
                    <ListItemText
                      sx={{ minWidth: 0, margin: 0 }}
                      primary={
                        <MDTypography variant="h6" fontWeight="medium">
                          Group
                        </MDTypography>
                      }
                      secondary={
                        <Typography
                          fontSize={14}
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: "block", wordBreak: "break-word" }}
                        >
                          Transfer to any active group agent.
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem divider disablePadding sx={{ my: 1 }}>
                  <ListItemButton
                    onClick={() => handleStepSelection("agent")}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: "8px",
                      minWidth: 0,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <MDTypography fontSize={32} color="info">
                        <Icon>support_agent</Icon>
                      </MDTypography>
                    </ListItemIcon>
                    <ListItemText
                      sx={{ minWidth: 0, margin: 0 }}
                      primary={
                        <MDTypography variant="h6" fontWeight="medium">
                          Agent
                        </MDTypography>
                      }
                      secondary={
                        <Typography
                          fontSize={14}
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: "block", wordBreak: "break-word" }}
                        >
                          Transfer to an active teammate
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding sx={{ my: 1 }}>
                  <ListItemButton
                    onClick={() => handleStepSelection("msisdn")}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: "8px",
                      minWidth: 0,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <MDTypography fontSize={32} color="success">
                        <Icon>phone_android</Icon>
                      </MDTypography>
                    </ListItemIcon>
                    <ListItemText
                      sx={{ minWidth: 0, margin: 0 }}
                      primary={
                        <MDTypography variant="h6" fontWeight="medium">
                          Phone Number
                        </MDTypography>
                      }
                      secondary={
                        <Typography
                          fontSize={14}
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: "block", wordBreak: "break-word" }}
                        >
                          Transfer to a mobile number
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            )}

            {drawerStep === "group" && (
              <MDBox p={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter specific groups..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ mr: 1, color: "text.secondary" }}>
                        search
                      </Icon>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <List
                  sx={{
                    maxHeight: { xs: 250, sm: "calc(100vh - 160px)" },
                    overflowY: "auto",
                  }}
                >
                  {filteredGroups.map((group) => (
                    <ListItem
                      key={group.reference}
                      disablePadding
                      onClick={() => executeTransfer("group", group.reference)}
                      divider
                      sx={{
                        cursor: "pointer",
                        my: 1,
                        py: 1.5,
                        px: 1,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <MDTypography variant="h6" fontWeight="regular">
                            {group.title}
                          </MDTypography>
                        }
                      />
                      <Icon>east</Icon>
                    </ListItem>
                  ))}
                  {filteredGroups.length === 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      No groups match your entry description.
                    </Typography>
                  )}
                </List>
              </MDBox>
            )}

            {drawerStep === "agent" && (
              <MDBox p={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search teammate name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ mr: 1, color: "text.secondary" }}>
                        search
                      </Icon>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                {loadingAgents ? (
                  <MDBox display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                  </MDBox>
                ) : (
                  <List
                    sx={{
                      maxHeight: { xs: 250, sm: "calc(100vh - 160px)" },
                      overflowY: "auto",
                    }}
                  >
                    {filteredAgents.map((agent) => (
                      <ListItem
                        key={agent.reference}
                        disablePadding
                        divider
                        sx={{
                          my: 1,
                          borderRadius: "8px",
                        }}
                      >
                        <ListItemButton
                          onClick={() =>
                            executeTransfer("agent", agent.reference)
                          }
                          sx={{
                            py: 1.5,
                            px: 2,
                            borderRadius: "8px",
                            minWidth: 0,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Icon color="info">support_agent</Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={agent.name || agent.email}
                            secondary={agent.email}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {filteredAgents.length === 0 && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ p: 2, textAlign: "center" }}
                      >
                        No active agents found.
                      </Typography>
                    )}
                  </List>
                )}
              </MDBox>
            )}

            {drawerStep === "msisdn" && (
              <Stack p={2} gap={1}>
                <MDInput
                  fullWidth
                  label="Phone Number"
                  value={msisdnValue}
                  onChange={(e) => setMsisdnValue(e.target.value)}
                  placeholder="+1234567890"
                  sx={{ mb: 3 }}
                />
                <MDButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!msisdnValue.trim()}
                  onClick={() => executeTransfer("msisdn", msisdnValue.trim())}
                >
                  Transfer Call
                </MDButton>
              </Stack>
            )}
          </MDBox>
        </MDBox>
      </SwipeableDrawer>

      <Footer />
    </DashboardLayout>
  );
}

export default CallCenterDialerPage;
